import { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { devError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 4 * 1024 * 1024; // 4 MB hard cap on the data URL we POST

const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(file);
  });

// Downscale via canvas to keep payloads small over mobile networks.
const downscale = (dataUrl, maxDim = 1280) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const CATEGORIES = [
  'doors', 'windows', 'flooring', 'bricks', 'lumber', 'roofing',
  'plumbing', 'lighting', 'fixtures', 'furniture', 'other'
];

export default function WingmanQuickAddPage() {
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const [photo, setPhoto] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('other');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const raw = await fileToDataURL(f);
      const small = await downscale(raw);
      if (small.length > MAX_BYTES) {
        toast.error('Photo too large after compression. Try a different one.');
        return;
      }
      setPhoto(small);
    } catch (err) {
      devError('photo read failed', err);
      toast.error("Couldn't read that photo");
    }
  };

  const submit = async () => {
    if (!photo)        { toast.error('Add a photo first');  return; }
    if (!title.trim()) { toast.error('Give it a title');    return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/wingman/quick-listing`, {
        title: title.trim(),
        image_data_url: photo,
        category,
        price: price ? Number(price) : null,
      });
      toast.success('Draft saved — finish it on desktop ✓');
      navigate('/wingman/inbox', { replace: true });
      return data;
    } catch (e) {
      devError('quick-listing failed', e);
      toast.error(e.response?.data?.detail || "Couldn't create draft listing");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 py-6" data-testid="wingman-quick-add">
      <h2 className="text-xl font-bold text-white mb-1">Quick add</h2>
      <p className="text-sm text-slate-400 mb-6">Snap a photo + give it a name. We'll save it as a draft — you finish details on the desktop.</p>

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="w-full aspect-square rounded-2xl border-2 border-dashed border-white/15 hover:border-[#D4AF37] flex flex-col items-center justify-center text-slate-400 overflow-hidden"
        data-testid="wingman-quick-add-photo-btn"
      >
        {photo ? (
          <img src={photo} alt="Listing preview" className="w-full h-full object-cover" />
        ) : (
          <>
            <Camera className="w-10 h-10 mb-3 text-[#D4AF37]" />
            <span className="text-sm">Tap to take a photo</span>
            <span className="text-[11px] text-slate-500 mt-1">or pick from gallery</span>
          </>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
        data-testid="wingman-quick-add-photo-input"
      />

      <div className="mt-5 space-y-3">
        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">Title *</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Victorian oak door"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-base focus:outline-none focus:border-[#D4AF37]"
            data-testid="wingman-quick-add-title"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-base focus:outline-none focus:border-[#D4AF37]"
            data-testid="wingman-quick-add-category"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">Price (optional)</span>
          <input
            type="number"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-base focus:outline-none focus:border-[#D4AF37]"
            data-testid="wingman-quick-add-price"
          />
        </label>
      </div>

      <button
        onClick={submit}
        disabled={busy || !photo || !title.trim()}
        className="mt-6 w-full py-3 rounded-full bg-[#D4AF37] text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
        data-testid="wingman-quick-add-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Save draft
      </button>
    </div>
  );
}
